import type { ReactNode } from 'react';
import React from 'react';
import PropTypes from 'prop-types';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import ThemeContext from 'components-stateless/Theme/ThemeContext';
import {
  selectWhiteLabelling,
  selectIsWhiteLabellingConfigured,
} from 'containers/WhiteLabelling/_utils/selectors';
import { defaultWhiteLabelling } from 'components-stateless/constants';
import { COLOURS_AA } from 'components-stateless/DesignSystem/Atoms/Colours';
import { createThemeWithWhiteLabelling } from 'constants/theme';
import { useFlag } from 'utils/flags/hooks';
import { FEATURE_TOP_LEVEL_REPORTING } from 'utils/flags/constants';

const propTypes = {
  isWhiteLabellingConfigured: PropTypes.bool,
  whiteLabelling: PropTypes.object,
  allowWhitelabelling: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

const mapStateToProps = createStructuredSelector({
  whiteLabelling: selectWhiteLabelling(),
  isWhiteLabellingConfigured: selectIsWhiteLabellingConfigured(),
});

const connector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof connector>;
type AllThemeProviderProps = PropsFromRedux & {
  children: ReactNode;
  allowWhitelabelling?: boolean;
};

const AllThemeProvider = ({
  whiteLabelling,
  isWhiteLabellingConfigured,
  allowWhitelabelling = false,
  children,
}: AllThemeProviderProps) => {
  const isWhiteLabellingObjectHasValue = Object.keys(whiteLabelling).length > 0;
  const isTopLevelReportingEnabled = useFlag(FEATURE_TOP_LEVEL_REPORTING);

  const resolvedConfig =
    allowWhitelabelling &&
    isWhiteLabellingConfigured &&
    isWhiteLabellingObjectHasValue
      ? whiteLabelling
      : defaultWhiteLabelling;

  // When the top-level reporting flag is on, swap the legacy default
  // navbar background (MEDIUM_BLUE) for BgGrey800. Customers who have
  // explicitly customised their navbar colour to anything other than
  // the legacy default keep their colour untouched.
  const whiteLabellingConfig =
    isTopLevelReportingEnabled &&
    resolvedConfig.navBarBackgroundColor ===
      defaultWhiteLabelling.navBarBackgroundColor
      ? {
          ...resolvedConfig,
          navBarBackgroundColor: COLOURS_AA.neutralBG.BgGrey800,
        }
      : resolvedConfig;

  return (
    <ThemeContext.Provider
      value={{
        ...whiteLabellingConfig,
        allowWhitelabelling,
      }}
    >
      <StyledEngineProvider injectFirst>
        <ThemeProvider
          theme={createThemeWithWhiteLabelling(whiteLabellingConfig)}
        >
          <>{children}</>
        </ThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  );
};

AllThemeProvider.propTypes = propTypes;

export default connector(AllThemeProvider);
